import React, { useEffect, useState, useCallback } from 'react';
import dayjs from 'dayjs';
import api from '../api/axios';
import ObjectTable from '../components/ObjectTable';
import DeviceFeedbackTable from '../components/DeviceFeedbackTable';
import { useDeviceSelection } from '../context/DeviceSelectionContext';
import './historyPage.css';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Box,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
} from '@mui/material';

import Collapse from '@mui/material/Collapse';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import WifiIcon from '@mui/icons-material/Wifi';
import UsbIcon from '@mui/icons-material/Usb';
import SettingsIcon from '@mui/icons-material/Settings';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// Derive a log's success/failure for the STATUS chip.
// Device Feedback is informational (always "Success"). Otherwise prefer the recorded
// deviceResponse.success; for Data ACK rows that predate that field, fall back to the
// ACK payload (status 0 = ACK/success) so they don't show a false "Failed".
const deriveLogSuccess = (log) => {
  if (!log) return false;
  if (log.action === 'Device Feedback') return true;
  if (log.deviceResponse && typeof log.deviceResponse.success === 'boolean') {
    return log.deviceResponse.success;
  }
  if (log.action === 'Data ACK' && log.parameters) {
    if (typeof log.parameters.ack === 'boolean') return log.parameters.ack;
    if (typeof log.parameters.status === 'number') return log.parameters.status === 0;
  }
  return false;
};

const HistoryPage = () => {
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  const [filterType, setFilterType] = useState('alltime');
  const [customStartDate, setCustomStartDate] = useState(dayjs().startOf('day'));
  const [customEndDate, setCustomEndDate] = useState(dayjs().endOf('day'));
  const [actionFilter, setActionFilter] = useState('');
  const [transportFilter, setTransportFilter] = useState('');
  const [logLimit, setLogLimit] = useState(50);
  const [expandedRows, setExpandedRows] = useState({});
  const [deviceOptions, setDeviceOptions] = useState([]);
  const { selectedDeviceId, setSelectedDeviceId } = useDeviceSelection();

  const role = sessionStorage.getItem('role') || 'user';

  const getTransportIcon = (transport) => {
    switch (transport) {
      case 'wifi':
        return <WifiIcon fontSize="small" />;
      case 'uart':
        return <UsbIcon fontSize="small" />;
      case 'tcp':
        return <SettingsIcon fontSize="small" />;
      default:
        return <SettingsIcon fontSize="small" />;
    }
  };

  const getActionColor = (action) => {
    if (!action) return 'default';
    if (action.includes('SW Parameters')) return 'primary';
    if (action.includes('Settings')) return 'secondary';
    if (action.includes('Debug')) return 'warning';
    return 'default';
  };

  const applyFilter = useCallback((data, type) => {
    const safeData = Array.isArray(data) ? data : [];
    const now = dayjs();
    let filtered = [];

    switch (type) {
      case 'today':
        filtered = safeData.filter((log) => dayjs(log.timestamp).isSame(now, 'day'));
        break;
      case '7days':
        filtered = safeData.filter((log) => dayjs(log.timestamp).isAfter(now.subtract(7, 'day')));
        break;
      case '30days':
        filtered = safeData.filter((log) => dayjs(log.timestamp).isAfter(now.subtract(30, 'day')));
        break;
      case 'custom':
        if (customStartDate && customEndDate) {
          const start = customStartDate.startOf('day');
          const end = customEndDate.endOf('day');
          filtered = safeData.filter((log) => {
            const d = dayjs(log.timestamp);
            return (d.isAfter(start) || d.isSame(start)) && (d.isBefore(end) || d.isSame(end));
          });
        } else {
          filtered = safeData;
        }
        break;
      case 'alltime':
      default:
        filtered = safeData;
    }

    if (actionFilter) filtered = filtered.filter((log) => log.action && log.action.includes(actionFilter));
    if (transportFilter) filtered = filtered.filter((log) => log.transportMode === transportFilter);
    if (selectedDeviceId) filtered = filtered.filter((log) => log.deviceId === selectedDeviceId);

    setFilteredLogs(filtered);
  }, [customStartDate, customEndDate, actionFilter, transportFilter, selectedDeviceId]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filterType === 'custom' && customStartDate && customEndDate) {
        params.append('startDate', customStartDate.toISOString());
        params.append('endDate', customEndDate.toISOString());
      }
      
      // Send action and transport filters to backend so limit is applied after filtering
      if (actionFilter) {
        params.append('action', actionFilter);
      }
      if (transportFilter) {
        params.append('transport', transportFilter);
      }
      
      params.append('limit', String(logLimit || 50));
      if (selectedDeviceId) params.append('deviceId', selectedDeviceId);

      let url = `/history/user?${params.toString()}`;
      if (role === 'admin' || role === 'superadmin') url = `/history/all?${params.toString()}`;

      const res = await api.get(url);
      const data = res.data || [];
      setLogs(data);
      applyFilter(data, filterType);
    } catch (err) {
      console.error('Error fetching logs:', err);
      setError('Failed to fetch logs. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [filterType, customStartDate, customEndDate, logLimit, role, applyFilter, actionFilter, transportFilter, selectedDeviceId]);

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType, logLimit, actionFilter, transportFilter, selectedDeviceId]);

  // Refetch when custom date range changes to immediately load the selected timeline
  useEffect(() => {
    if (filterType === 'custom' && customStartDate && customEndDate) {
      fetchLogs();
    }
  }, [customStartDate, customEndDate, filterType, fetchLogs]);

  useEffect(() => {
    const fetchDevices = async () => {
      try {
        const res = await api.get('/device/devices/registered');
        const list = Array.isArray(res.data) ? res.data : [];
        const ids = list.map(d => ({ id: d.deviceId, name: d.customName || d.deviceId }));
        setDeviceOptions(ids);
      } catch (e) {
        try {
          const res = await api.get('/device/devices/online');
          const list = Array.isArray(res.data) ? res.data : [];
          const ids = list.map(d => (typeof d === 'string' ? { id: d, name: d } : { id: d.deviceId || d, name: d.deviceId || d }));
          setDeviceOptions(ids);
        } catch (err) {
          setDeviceOptions([]);
        }
      }
    };
    fetchDevices();
  }, []);

  const handleFilterChange = (event) => {
    const newFilter = event.target.value;
    setFilterType(newFilter);
    applyFilter(logs, newFilter);
  };

  const handleCustomDateChange = () => applyFilter(logs, 'custom');

  const toggleRowExpansion = (logId) => setExpandedRows((prev) => ({ ...prev, [logId]: !prev[logId] }));

  const exportToCSV = async () => {
    // Use the streaming CSV export endpoint for efficient large exports
    setExporting(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filterType === 'custom' && customStartDate && customEndDate) {
        params.append('startDate', customStartDate.toISOString());
        params.append('endDate', customEndDate.toISOString());
      }
      if (actionFilter) params.append('action', actionFilter);
      if (transportFilter) params.append('transport', transportFilter);

      // Use axios with responseType blob to handle the CSV stream
      const response = await api.get(`/history/export?${params.toString()}`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'text/csv' });
      const urlBlob = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = urlBlob;
      a.download = `history_logs_${dayjs().format('YYYY-MM-DD')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(urlBlob);
    } catch (err) {
      console.error('Error exporting logs:', err);
      setError('Failed to export logs. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const renderElementDetails = (log) => {
    const actionsWithElements = ['Send SW Parameters'];
    if (!actionsWithElements.includes(log.action)) return null;
    if (!Array.isArray(log.elements) || log.elements.length === 0) return <Typography variant="body2" color="text.secondary">No elements data</Typography>;

    return (
      <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>No.</TableCell>
              <TableCell>Symbol</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Quantity</TableCell>
              <TableCell>Vout Base</TableCell>
              <TableCell>Frequency</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {log.elements.map((element, index) => (
              <TableRow key={index}>
                <TableCell>{index + 1}</TableCell>
                <TableCell><Chip label={element.symbol} size="small" /></TableCell>
                <TableCell>{element.name || element.symbol}</TableCell>
                <TableCell>{element.quantity}</TableCell>
                <TableCell>{element.vout_base}</TableCell>
                <TableCell>{element.freq}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  if (loading) return (<Paper sx={{ p: 3 }}><Typography>Loading history...</Typography></Paper>);
  if (error) return (<Paper sx={{ p: 3 }}><Typography color="error">{error}</Typography><Button onClick={fetchLogs} sx={{ mt: 2 }}>Retry</Button></Paper>);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5">Activity History</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {exporting && (
                <Typography variant="body2" color="primary" sx={{ mr: 1 }}>
                  Exporting all logs...
                </Typography>
              )}
              <Tooltip title="Refresh">
                <IconButton onClick={fetchLogs} disabled={loading || exporting}><RefreshIcon /></IconButton>
              </Tooltip>
              <Tooltip title={exporting ? "Exporting..." : "Export to CSV (all matching logs)"}>
                <IconButton onClick={exportToCSV} disabled={!logs || logs.length === 0 || exporting || loading}>
                  {exporting ? <RefreshIcon className="rotating" /> : <DownloadIcon />}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} sm={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Log Limit</InputLabel>
                <Select value={logLimit} label="Log Limit" onChange={(e) => setLogLimit(Number(e.target.value))}>
                  {[50, 100, 200, 300, 500, 1000].map((val) => <MenuItem key={val} value={val}>{val}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Time Filter</InputLabel>
                <Select value={filterType} label="Time Filter" onChange={handleFilterChange}>
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="7days">Last 7 Days</MenuItem>
                  <MenuItem value="30days">Last 30 Days</MenuItem>
                  <MenuItem value="custom">Custom Range</MenuItem>
                  <MenuItem value="alltime">All Time</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Action Filter</InputLabel>
                <Select value={actionFilter} label="Action Filter" onChange={(e) => setActionFilter(e.target.value)}>
                  <MenuItem value="">All Actions</MenuItem>
                  <MenuItem value="Send SW Parameters">SW Parameters</MenuItem>
                  <MenuItem value="Set Device Settings">Device Settings</MenuItem>
                  <MenuItem value="Get Device Settings">Get Device Settings</MenuItem>
                  <MenuItem value="Device Feedback">Device Feedback</MenuItem>
                  <MenuItem value="Debug Packet">Debug</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Transport</InputLabel>
                <Select value={transportFilter} label="Transport" onChange={(e) => setTransportFilter(e.target.value)}>
                  <MenuItem value="">All Transport</MenuItem>
                  <MenuItem value="wifi">WiFi</MenuItem>
                  <MenuItem value="uart">UART</MenuItem>
                  <MenuItem value="tcp">TCP</MenuItem>
                  <MenuItem value="auto">Auto</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Device</InputLabel>
                <Select
                  value={selectedDeviceId || ''}
                  label="Device"
                  onChange={(e) => setSelectedDeviceId(e.target.value)}
                >
                  <MenuItem value="">All Devices</MenuItem>
                  {deviceOptions.map(opt => (
                    <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {filterType === 'custom' && (
              <>
                <Grid item xs={12} sm={2}>
                  <DatePicker
                    label="Start Date"
                    value={customStartDate}
                    onChange={(newValue) => { setCustomStartDate(newValue); if (customEndDate) setTimeout(handleCustomDateChange, 100); }}
                    renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                    maxDate={customEndDate || dayjs()}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <DatePicker
                    label="End Date"
                    value={customEndDate}
                    onChange={(newValue) => { setCustomEndDate(newValue); if (customStartDate) setTimeout(handleCustomDateChange, 100); }}
                    renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                    minDate={customStartDate}
                    maxDate={dayjs()}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} sm={2}>
              <Chip label={`${filteredLogs.length} logs found`} color="primary" variant="outlined" />
            </Grid>
          </Grid>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Showing: {filterType === 'custom' ? `${customStartDate.format('MMM DD, YYYY')} - ${customEndDate.format('MMM DD, YYYY')}` : filterType}
          </Typography>
        </Box>

        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Device ID</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Transport</TableCell>
                <TableCell>Elements</TableCell>
                <TableCell>Timestamp</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(!filteredLogs || filteredLogs.length === 0) ? (
                <TableRow>
                  <TableCell colSpan={8} align="center"><Typography>No logs found for the selected filters</Typography></TableCell>
                </TableRow>
              ) : (
                filteredLogs.map((log, index) => (
                  <React.Fragment key={log._id || index}>
                    <TableRow>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium" sx={{ wordBreak: 'break-all', whiteSpace: 'pre-wrap', maxWidth: 120 }}>
                          {log.deviceId ? String(log.deviceId).replace(/(.{12})/g, '$1\n') : 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          {log.user ? (
                            <>
                              <Typography variant="body2" fontWeight="medium">{log.user.username}</Typography>
                              <Chip label={log.user.role} size="small" variant="outlined" color="secondary" />
                            </>
                          ) : log.userId ? (
                            <Typography variant="body2" fontWeight="medium">{log.userId}</Typography>
                          ) : (
                            // Device-originated events (heartbeat, ACK, feedback) have no
                            // human user — label them clearly rather than leaving a blank cell.
                            <Chip label="Device" size="small" variant="outlined" color="default"
                              sx={{ fontStyle: 'italic', color: 'text.secondary' }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell><Chip label={log.action || 'Device Action'} color={getActionColor(log.action)} size="small" /></TableCell>
                      <TableCell><Chip icon={getTransportIcon(log.transportMode)} label={log.transportMode || 'auto'} size="small" variant="outlined" /></TableCell>
                      <TableCell>
                        <Box>
                          {(() => {
                            const computedCount = (Number.isInteger(log.elementCount) && log.elementCount > 0) ? log.elementCount : (Array.isArray(log.elements) ? log.elements.length : 0);
                            return (
                              <>
                                <Typography variant="body2">{computedCount} elements</Typography>
                                {log.totalQuantity > 0 && <Typography variant="caption" color="text.secondary">Total: {log.totalQuantity}</Typography>}
                              </>
                            );
                          })()}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{dayjs(log.timestamp).format('MMM DD, YYYY')}</Typography>
                        <Typography variant="caption" color="text.secondary">{dayjs(log.timestamp).format('HH:mm:ss')}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={deriveLogSuccess(log) ? 'Success' : 'Failed'}
                          color={deriveLogSuccess(log) ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => toggleRowExpansion(log._id || index)}>
                          <ExpandMoreIcon sx={{ transform: expandedRows[log._id || index] ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                        <Collapse in={expandedRows[log._id || index]} timeout="auto" unmountOnExit>
                          <Box sx={{ margin: 1 }}>
                            <Typography variant="h6" gutterBottom>Details</Typography>

                            {log.action === 'Device Feedback' && log.parameters && (
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle1" gutterBottom>Device Feedback Data</Typography>
                                <DeviceFeedbackTable feedback={log.parameters} />
                              </Box>
                            )}

                            {(() => {
                              const actionsWithElements = ['Send SW Parameters'];
                              if (!actionsWithElements.includes(log.action)) return null;
                              const elemCount = Array.isArray(log.elements) ? log.elements.length : (log.elementCount || 0);
                              return (
                                <Box sx={{ mb: 2 }}>
                                  <Typography variant="subtitle1" gutterBottom>Elements Sent ({elemCount} total)</Typography>
                                  {renderElementDetails(log)}
                                </Box>
                              );
                            })()}

                            {log.settings && Object.keys(log.settings).length > 0 && (
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle1" gutterBottom>Device Settings</Typography>
                                <ObjectTable data={log.settings} />
                              </Box>
                            )}

                            {log.action === 'Data ACK' && log.parameters && (
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle1" gutterBottom>ACK Response</Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                  <Chip
                                    label={deriveLogSuccess(log) ? 'ACK — status 0' : `NACK — status ${log.parameters.status ?? '?'}`}
                                    color={deriveLogSuccess(log) ? 'success' : 'error'}
                                    variant="outlined"
                                  />
                                  {log.parameters.message && <Chip label={`message: ${log.parameters.message}`} variant="outlined" />}
                                  {log.parameters.raw && <Chip label={`raw: ${log.parameters.raw}`} variant="outlined" />}
                                </Box>
                              </Box>
                            )}

                            {log.deviceResponse && log.action !== 'Device Feedback' && log.action !== 'Data ACK' && (
                              <Box>
                                <Typography variant="subtitle1" gutterBottom>Device Response</Typography>
                                <Chip label={log.deviceResponse.message || 'No message'} color={log.deviceResponse.success ? 'success' : 'error'} variant="outlined" />
                              </Box>
                            )}

                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </LocalizationProvider>
  );
};

export default HistoryPage;

